import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  InputAdornment,
  IconButton,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Paper,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Group as GroupIcon,
  Add as AddIcon,
  VolumeOff as VolumeOffIcon,
  VolumeUp as VolumeUpIcon,
  InsertDriveFile as FileIcon,
  Search as SearchIcon,
} from '@mui/icons-material'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { useChatStore } from '@/stores/chatStore'
import { useAuthStore } from '@/stores/authStore'
import { chatApi } from '@/api/chat'
import type { ChatAttachment, ChatContact, ChatMessage, ChatRoom } from '@/api/chat'
import { getErrorMessage } from '@/utils/error'
import { unlockChatSound } from '@/utils/chatSound'
import { storagePublicUrl } from '@/utils/storage'
import '@/styles/dashboard.css'

const MUTE_UNTIL_FAR_FUTURE = '2099-12-31T23:59:59.000Z'
const IMAGE_MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024
const FILE_MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024
const ACCEPTED_FILE_TYPES =
  'image/jpeg,image/png,image/webp,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip'

/** Высота верхней строки панели: «Новый чат» слева = заголовок чата справа. */
const CHAT_TOOLBAR_ROW_SX = {
  px: 1.5,
  py: 1.5,
  borderBottom: 1,
  borderColor: 'divider',
  height: 64,
  display: 'flex',
  alignItems: 'center',
  boxSizing: 'border-box',
} as const

export function ChatPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const params = useParams<{ userId?: string; groupId?: string; roomId?: string }>()
  const currentUser = useAuthStore((s) => s.user)
  const currentUserId = currentUser?.id ?? 0

  const {
    rooms,
    contacts,
    currentRoomId,
    byRoom,
    isLoadingRooms,
    isLoadingMessages,
    isLoadingContacts,
    error,
    fetchRooms,
    fetchContacts,
    openDirect,
    setCurrentRoom,
    fetchMessages,
    sendMessage,
    editMessage,
    deleteMessage,
    markRead,
    setRoomMuted,
    clearError,
  } = useChatStore()

  const [deepLinkError, setDeepLinkError] = useState<string | null>(null)
  const [text, setText] = useState('')
  const [pendingAttachments, setPendingAttachments] = useState<ChatAttachment[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [infoOpen, setInfoOpen] = useState(false)
  const [newChatOpen, setNewChatOpen] = useState(false)
  const [isMuting, setIsMuting] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)
  const [roomSearchQuery, setRoomSearchQuery] = useState('')
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const listEndRef = useRef<HTMLDivElement | null>(null)
  const dragDepthRef = useRef(0)

  useEffect(() => {
    void fetchRooms()
  }, [fetchRooms])

  useEffect(() => {
    if (!params.roomId) return
    const roomId = Number(params.roomId)
    if (!Number.isFinite(roomId)) return
    setCurrentRoom(roomId)
    navigate('/chat', { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.roomId])

  useEffect(() => {
    if (!params.userId) return
    const userId = Number(params.userId)
    if (!Number.isFinite(userId)) return
    openDirect(userId)
      .then(() => navigate('/chat', { replace: true }))
      .catch((e) => setDeepLinkError(getErrorMessage(e)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.userId])

  useEffect(() => {
    if (!params.groupId) return
    const groupId = Number(params.groupId)
    if (!Number.isFinite(groupId)) return
    const room = rooms.find((r) => r.student_group_id === groupId)
    if (room) {
      setCurrentRoom(room.id)
      navigate('/chat', { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.groupId, rooms])

  const currentRoom = useMemo(
    () => rooms.find((r) => r.id === currentRoomId) ?? null,
    [rooms, currentRoomId]
  )
  const roomState = currentRoomId ? byRoom[currentRoomId] : undefined
  const messages = roomState?.messages ?? []
  const canWrite = currentRoom?.can_write ?? false

  const filteredRooms = useMemo(
    () => filterRoomsBySearch(rooms, roomSearchQuery),
    [rooms, roomSearchQuery]
  )

  useEffect(() => {
    if (currentRoomId && !byRoom[currentRoomId]?.loaded) {
      void fetchMessages(currentRoomId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRoomId])

  useEffect(() => {
    if (!currentRoomId) return
    const roomId = currentRoomId
    const heartbeat = () => {
      void chatApi.enterPresence(roomId).catch(() => {})
    }
    heartbeat()
    const intervalId = window.setInterval(heartbeat, 30_000)
    return () => {
      window.clearInterval(intervalId)
      void chatApi.leavePresence(roomId).catch(() => {})
    }
  }, [currentRoomId])

  useEffect(() => {
    dragDepthRef.current = 0
    setIsDragOver(false)
  }, [currentRoomId, canWrite])

  useEffect(() => {
    if (!currentRoomId || messages.length === 0) return
    listEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    const lastId = messages[messages.length - 1]?.id
    if (lastId) void markRead(currentRoomId, lastId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, currentRoomId])

  const handleSelectRoom = (roomId: number) => {
    setEditingId(null)
    setText('')
    setPendingAttachments([])
    setCurrentRoom(roomId)
  }

  const handleOpenNewChat = async () => {
    unlockChatSound()
    setNewChatOpen(true)
    if (contacts.length === 0) {
      await fetchContacts()
    }
  }

  const handleSelectContact = async (contact: ChatContact) => {
    setNewChatOpen(false)
    setDeepLinkError(null)
    try {
      if (contact.existing_room_id) {
        setCurrentRoom(contact.existing_room_id)
      } else {
        await openDirect(contact.user.id)
      }
    } catch (e) {
      setDeepLinkError(getErrorMessage(e))
    }
  }

  const handleToggleMute = async () => {
    if (!currentRoomId || !currentRoom) return
    setIsMuting(true)
    try {
      const nextMuted = !currentRoom.is_muted
      await chatApi.mute(currentRoomId, nextMuted ? MUTE_UNTIL_FAR_FUTURE : null)
      setRoomMuted(currentRoomId, nextMuted)
    } catch (e) {
      setDeepLinkError(getErrorMessage(e))
    } finally {
      setIsMuting(false)
    }
  }

  const uploadFiles = async (files: File[]) => {
    if (!currentRoomId || !canWrite || files.length === 0) return

    unlockChatSound()
    setDeepLinkError(null)
    setIsUploading(true)
    try {
      for (const file of files) {
        const maxSize = file.type.startsWith('image/')
          ? IMAGE_MAX_FILE_SIZE_BYTES
          : FILE_MAX_FILE_SIZE_BYTES
        if (file.size > maxSize) {
          setDeepLinkError(
            file.type.startsWith('image/')
              ? t('chat.imageTooLarge')
              : t('chat.fileTooLarge')
          )
          continue
        }
        const attachment = await chatApi.uploadFile(currentRoomId, file)
        setPendingAttachments((prev) => [...prev, attachment])
      }
    } catch (e) {
      setDeepLinkError(getErrorMessage(e))
    } finally {
      setIsUploading(false)
    }
  }

  const handleFilePick = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files
    event.target.value = ''
    if (!fileList?.length) return
    await uploadFiles(Array.from(fileList))
  }

  const handleDragEnter = (event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    if (!canWrite || !currentRoomId) return
    if (!event.dataTransfer.types.includes('Files')) return
    dragDepthRef.current += 1
    setIsDragOver(true)
  }

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    dragDepthRef.current -= 1
    if (dragDepthRef.current <= 0) {
      dragDepthRef.current = 0
      setIsDragOver(false)
    }
  }

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    if (canWrite && currentRoomId) {
      event.dataTransfer.dropEffect = 'copy'
    }
  }

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    dragDepthRef.current = 0
    setIsDragOver(false)
    if (!canWrite || !currentRoomId) return
    const files = Array.from(event.dataTransfer.files)
    void uploadFiles(files)
  }

  const handleSend = async () => {
    if (!currentRoomId || !canWrite) return
    const trimmed = text.trim()
    if (!trimmed && pendingAttachments.length === 0) return

    const payload = {
      text: trimmed || null,
      attachments: pendingAttachments.map((a) => ({
        type: a.type,
        path: a.path,
        name: a.name,
        mime: a.mime ?? 'application/octet-stream',
        size: a.size ?? 0,
      })),
    }

    try {
      if (editingId) {
        await editMessage(currentRoomId, editingId, payload)
        setEditingId(null)
      } else {
        await sendMessage(currentRoomId, payload)
      }
      setText('')
      setPendingAttachments([])
    } catch (e) {
      setDeepLinkError(getErrorMessage(e))
    }
  }

  const startEdit = (message: ChatMessage) => {
    setEditingId(message.id)
    setText(message.text ?? '')
    setPendingAttachments([])
  }

  const handleDelete = async (message: ChatMessage) => {
    if (!currentRoomId) return
    try {
      await deleteMessage(currentRoomId, message.id)
    } catch (e) {
      setDeepLinkError(getErrorMessage(e))
    }
  }

  return (
    <DashboardLayout>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" component="h1" fontWeight={700}>
          {t('chat.title')}
        </Typography>
      </Box>

      {(error || deepLinkError) && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => {
            setDeepLinkError(null)
            clearError()
          }}
        >
          {deepLinkError || error}
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(280px, 340px) 1fr' },
          gap: 2,
          height: { md: 'calc(100vh - 140px)' },
          width: '100%',
        }}
      >
        <Paper variant="outlined" sx={{ overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          <Box sx={CHAT_TOOLBAR_ROW_SX}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => void handleOpenNewChat()}
              sx={{ height: 40, textTransform: 'none' }}
            >
              {t('chat.newChat')}
            </Button>
          </Box>

          <Box sx={{ px: 1.5, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            <TextField
              size="small"
              fullWidth
              placeholder={t('chat.searchRooms')}
              value={roomSearchQuery}
              onChange={(e) => setRoomSearchQuery(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { height: 40 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: roomSearchQuery ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setRoomSearchQuery('')} edge="end">
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Box>

          {isLoadingRooms ? (
            <Box sx={{ p: 2 }}>
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} height={56} sx={{ mb: 1 }} />
              ))}
            </Box>
          ) : rooms.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <Typography color="text.secondary">{t('chat.noRooms')}</Typography>
            </Box>
          ) : filteredRooms.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <Typography color="text.secondary">{t('chat.noSearchResults')}</Typography>
            </Box>
          ) : (
            <List disablePadding sx={{ flex: 1, overflow: 'auto' }}>
              {filteredRooms.map((room) => (
                <ListItemButton
                  key={room.id}
                  selected={room.id === currentRoomId}
                  onClick={() => handleSelectRoom(room.id)}
                >
                  <ListItemAvatar>
                    <Badge color="primary" badgeContent={room.unread_count} overlap="circular">
                      <Avatar src={roomListAvatarUrl(room, currentUserId) ?? undefined}>
                        {room.type === 'group' ? <GroupIcon /> : (room.title?.[0] ?? '?')}
                      </Avatar>
                    </Badge>
                  </ListItemAvatar>
                  <ListItemText
                    primary={room.title ?? t('chat.untitled')}
                    secondary={renderPreview(room, t)}
                    primaryTypographyProps={{ noWrap: true }}
                    secondaryTypographyProps={{ noWrap: true }}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </Paper>

        <Paper
          variant="outlined"
          sx={{ display: 'flex', flexDirection: 'column', minHeight: 400, position: 'relative' }}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          {isDragOver && canWrite && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'action.hover',
                border: 2,
                borderColor: 'primary.main',
                borderStyle: 'dashed',
                borderRadius: 1,
                pointerEvents: 'none',
              }}
            >
              <Typography color="primary" fontWeight={600}>
                {t('chat.dropToAttach')}
              </Typography>
            </Box>
          )}
          {!currentRoom ? (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography color="text.secondary">{t('chat.selectRoom')}</Typography>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  ...CHAT_TOOLBAR_ROW_SX,
                  justifyContent: 'space-between',
                  gap: 1,
                }}
              >
                <Typography
                  fontWeight={600}
                  sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                  onClick={() => setInfoOpen(true)}
                >
                  {currentRoom.title ?? t('chat.untitled')}
                </Typography>
                <Tooltip title={currentRoom.is_muted ? t('chat.muteOn') : t('chat.muteOff')}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => void handleToggleMute()}
                      disabled={isMuting}
                    >
                      {currentRoom.is_muted ? <VolumeOffIcon /> : <VolumeUpIcon />}
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>

              <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                {roomState?.hasMore && (
                  <Box sx={{ textAlign: 'center', mb: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => currentRoomId && fetchMessages(currentRoomId, { loadMore: true })}
                    >
                      <Typography variant="caption">{t('chat.loadMore')}</Typography>
                    </IconButton>
                  </Box>
                )}
                {isLoadingMessages && messages.length === 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {[0, 1, 2, 3].map((i) => (
                      <Skeleton
                        key={i}
                        variant="rounded"
                        height={48}
                        width={i % 2 === 0 ? '70%' : '55%'}
                        sx={{ alignSelf: i % 2 === 0 ? 'flex-start' : 'flex-end' }}
                      />
                    ))}
                  </Box>
                ) : (
                  messages.map((message) => (
                    <MessageBubble
                      key={message.id}
                      message={message}
                      isOwn={message.author_user_id === currentUserId}
                      showAuthor={currentRoom.type === 'group'}
                      canWrite={canWrite}
                      onEdit={startEdit}
                      onDelete={handleDelete}
                      t={t}
                    />
                  ))
                )}
                <div ref={listEndRef} />
              </Box>

              <Box sx={{ borderTop: 1, borderColor: 'divider', p: 1.5 }}>
                {!canWrite && (
                  <Alert severity="info" sx={{ mb: 1 }}>
                    {t('chat.readOnly')}
                  </Alert>
                )}

                {pendingAttachments.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    {pendingAttachments.map((a) => (
                      <Box key={a.path} sx={{ position: 'relative' }}>
                        {a.type === 'image' ? (
                          <img
                            src={storagePublicUrl(a.path)}
                            alt={a.name}
                            style={{ height: 56, borderRadius: 6, objectFit: 'cover' }}
                          />
                        ) : (
                          <Chip icon={<FileIcon />} label={a.name} size="small" />
                        )}
                        <IconButton
                          size="small"
                          sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper' }}
                          onClick={() =>
                            setPendingAttachments((prev) => prev.filter((x) => x.path !== a.path))
                          }
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}

                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPTED_FILE_TYPES}
                    multiple
                    hidden
                    disabled={!canWrite}
                    onChange={handleFilePick}
                  />
                  <Tooltip title={t('chat.attachFile')}>
                    <span>
                      <IconButton
                        onClick={() => fileInputRef.current?.click()}
                        disabled={!canWrite || isUploading}
                      >
                        {isUploading ? <CircularProgress size={20} /> : <AttachFileIcon />}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    maxRows={4}
                    placeholder={
                      canWrite ? t('chat.messagePlaceholder') : t('chat.readOnly')
                    }
                    value={text}
                    disabled={!canWrite}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey && canWrite) {
                        e.preventDefault()
                        void handleSend()
                      }
                    }}
                  />
                  <IconButton
                    color="primary"
                    onClick={() => void handleSend()}
                    disabled={!canWrite}
                  >
                    <SendIcon />
                  </IconButton>
                </Box>

                {editingId && (
                  <Box sx={{ mt: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {t('chat.editingHint')}{' '}
                      <Box
                        component="span"
                        sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={() => {
                          setEditingId(null)
                          setText('')
                        }}
                      >
                        {t('chat.cancel')}
                      </Box>
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          )}
        </Paper>
      </Box>

      <Dialog open={infoOpen} onClose={() => setInfoOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {currentRoom?.type === 'group' ? t('chat.groupInfo') : t('chat.participantInfo')}
        </DialogTitle>
        <DialogContent dividers>
          {(currentRoom?.members ?? []).map((member) => (
            <Box key={member.user_id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Avatar src={member.user?.avatar_url ?? undefined} sx={{ width: 40, height: 40 }}>
                {member.user?.full_name?.[0] ?? '?'}
              </Avatar>
              <Box>
                <Typography variant="body2">{member.user?.full_name ?? `#${member.user_id}`}</Typography>
                {member.role === 'admin' && (
                  <Typography variant="caption" color="text.secondary">
                    {t('chat.roleAdmin')}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </DialogContent>
      </Dialog>

      <Dialog open={newChatOpen} onClose={() => setNewChatOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('chat.selectContact')}</DialogTitle>
        <DialogContent dividers>
          {isLoadingContacts ? (
            <List disablePadding>
              {[0, 1, 2].map((i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
                  <Skeleton variant="circular" width={40} height={40} />
                  <Skeleton variant="text" width="60%" />
                </Box>
              ))}
            </List>
          ) : contacts.length === 0 ? (
            <Typography color="text.secondary">{t('chat.noContacts')}</Typography>
          ) : (
            <List disablePadding>
              {contacts.map((contact) => (
                <ListItemButton
                  key={contact.user.id}
                  onClick={() => void handleSelectContact(contact)}
                >
                  <ListItemAvatar>
                    <Avatar src={contact.user.avatar_url ?? undefined}>
                      {contact.user.full_name?.[0] ?? '?'}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText primary={contact.user.full_name} />
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  )
}

function filterRoomsBySearch(rooms: ChatRoom[], query: string): ChatRoom[] {
  const normalized = query.trim().toLocaleLowerCase()
  if (!normalized) return rooms

  return rooms.filter((room) => roomMatchesSearch(room, normalized))
}

function roomMatchesSearch(room: ChatRoom, normalizedQuery: string): boolean {
  const parts: string[] = []

  if (room.title) parts.push(room.title)
  if (room.group?.name) parts.push(room.group.name)

  for (const member of room.members ?? []) {
    if (member.user?.full_name) parts.push(member.user.full_name)
  }

  const lastMessage = room.last_message
  if (lastMessage && !lastMessage.is_deleted) {
    if (lastMessage.text) parts.push(lastMessage.text)
    for (const attachment of lastMessage.attachments) {
      if (attachment.name) parts.push(attachment.name)
    }
  }

  const haystack = parts.join(' ').toLocaleLowerCase()
  return haystack.includes(normalizedQuery)
}

function roomListAvatarUrl(room: ChatRoom, currentUserId: number): string | null {
  if (room.type === 'group') {
    return null
  }
  const other = room.members?.find((m) => m.user_id !== currentUserId)
  return other?.user?.avatar_url ?? null
}

function renderPreview(room: ChatRoom, t: (key: string) => string): string {
  const message = room.last_message
  if (!message) return t('chat.noMessages')
  if (message.is_deleted) return t('chat.messageDeleted')
  if (message.text) return message.text
  const firstAttachment = message.attachments[0]
  if (firstAttachment?.type === 'file') return firstAttachment.name || t('chat.filePreview')
  return t('chat.imagePreview')
}

interface MessageBubbleProps {
  message: ChatMessage
  isOwn: boolean
  showAuthor: boolean
  canWrite: boolean
  onEdit: (message: ChatMessage) => void
  onDelete: (message: ChatMessage) => void
  t: (key: string) => string
}

function MessageBubble({
  message,
  isOwn,
  showAuthor,
  canWrite,
  onEdit,
  onDelete,
  t,
}: MessageBubbleProps) {
  const authorAvatar = message.author?.avatar_url ?? undefined

  return (
    <Box sx={{ display: 'flex', justifyContent: isOwn ? 'flex-end' : 'flex-start', mb: 1, gap: 1 }}>
      {!isOwn && (
        <Avatar src={authorAvatar} sx={{ width: 32, height: 32, mt: 0.25 }}>
          {message.author?.full_name?.[0] ?? '?'}
        </Avatar>
      )}
      <Box
        sx={{
          maxWidth: '75%',
          bgcolor: isOwn ? 'primary.main' : 'action.hover',
          color: isOwn ? 'primary.contrastText' : 'text.primary',
          borderRadius: 2,
          px: 1.5,
          py: 1,
        }}
      >
        {showAuthor && !isOwn && message.author && (
          <Typography variant="caption" fontWeight={600} display="block">
            {message.author.full_name}
          </Typography>
        )}
        {message.is_deleted ? (
          <Typography variant="body2" fontStyle="italic">
            {t('chat.messageDeleted')}
          </Typography>
        ) : (
          <>
            {message.attachments.map((a) => (
              <Box key={a.path} sx={{ mb: message.text ? 0.5 : 0 }}>
                {a.type === 'image' ? (
                  <img
                    src={storagePublicUrl(a.path)}
                    alt={a.name}
                    style={{ maxWidth: '100%', borderRadius: 6, display: 'block' }}
                  />
                ) : (
                  <Chip
                    component="a"
                    href={storagePublicUrl(a.path)}
                    target="_blank"
                    rel="noopener noreferrer"
                    clickable
                    icon={<FileIcon />}
                    label={`${a.name} · ${t('chat.download')}`}
                    size="small"
                    sx={{
                      bgcolor: isOwn ? 'primary.dark' : 'background.paper',
                      color: 'inherit',
                      mb: 0.5,
                    }}
                  />
                )}
              </Box>
            ))}
            {message.text && (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {message.text}
              </Typography>
            )}
          </>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
          <Typography variant="caption" sx={{ opacity: 0.7 }}>
            {formatTime(message.created_at)}
            {message.edited_at ? ` · ${t('chat.edited')}` : ''}
          </Typography>
          {isOwn && !message.is_deleted && canWrite && (
            <>
              <IconButton size="small" sx={{ color: 'inherit', opacity: 0.7 }} onClick={() => onEdit(message)}>
                <EditIcon sx={{ fontSize: 14 }} />
              </IconButton>
              <IconButton size="small" sx={{ color: 'inherit', opacity: 0.7 }} onClick={() => onDelete(message)}>
                <DeleteIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </>
          )}
        </Box>
      </Box>
    </Box>
  )
}

function formatTime(iso: string | null): string {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  } catch {
    return ''
  }
}
